import React, { useState } from "react";
import styled from "@emotion/styled";
import { QueryResult } from "react-query";
import { TextField, TextFieldChangeEvent } from "../TextField";
import { Typography } from "../Typography";
import { CouponCodeResult } from "@ecologi/shared";

export type CouponCodeFieldProps = {
  couponCodeResult: QueryResult<CouponCodeResult>;
  currencySymbol: any;
  onBlur: any;
  onChange: (e: TextFieldChangeEvent) => void;
  value: string;
  isCouponValid: boolean;
  isCouponStatusUncertain: boolean;
};

const CouponInputContainer = styled.div`
  margin: 20px 0;
`;

const CouponInputLink = styled.a`
  color: ${({ theme }) => theme.colors.scarlet};
  opacity: 0.7;
  display: inline-block;
  margin: 20px 0;

  &:hover {
    opacity: 1;
  }
`;

const GiftCodeText = styled(Typography)`
  color: ${({ theme }) => theme.colors.scarlet};
  margin-top: 4px;
`;

export const CouponCodeField = ({
  couponCodeResult,
  onBlur,
  onChange,
  value,
  isCouponValid,
  isCouponStatusUncertain,
}: CouponCodeFieldProps) => {
  const [isCouponCodeFieldVisible, setIsCouponCodeFieldVisible] =
    useState(false);

  const shouldBeRedeemedAsAGift = couponCodeResult.data?.isGiftingCode ?? false;

  const VALID_TEXT = "Coupon applied.";
  const INVALID_TEXT = "Coupon code is invalid.";
  const GIFT_CODE_TEXT = "This seems to be a gift code!";

  return isCouponCodeFieldVisible ? (
    <CouponInputContainer>
      <TextField
        errorText={shouldBeRedeemedAsAGift ? GIFT_CODE_TEXT : INVALID_TEXT}
        isInvalid={
          value.length > 0 &&
          !isCouponValid &&
          !couponCodeResult.isLoading &&
          !couponCodeResult.isInitialData &&
          !isCouponStatusUncertain
        }
        isLoading={couponCodeResult.isLoading}
        isValid={value.length > 0 && isCouponValid && !isCouponStatusUncertain}
        label="Coupon code"
        name="couponCode"
        onBlur={onBlur}
        onChange={onChange}
        successText={VALID_TEXT}
        value={value}
      />
      {shouldBeRedeemedAsAGift && !isCouponStatusUncertain && (
        <GiftCodeText use="label">
          {` Please redeem it on our `}
          <Typography as="a" use="inlineLink" href={"/redeem"}>
            gift code
          </Typography>
          {` page.`}
        </GiftCodeText>
      )}
    </CouponInputContainer>
  ) : (
    <CouponInputLink
      href="#redeem"
      onClick={(e) => {
        e.preventDefault();
        setIsCouponCodeFieldVisible(true);
      }}
    >
      Redeem a coupon code
    </CouponInputLink>
  );
};
